/**
 * Type-safe utilities for parsing React Router location.state.
 * Eliminates unsafe `location.state as X` casts throughout the codebase.
 */

/** Common assessment navigation state passed between evaluation pages */
export interface AssessmentNavState {
  employeeId?: string;
  competencyIds?: string[];
  evaluatedCompetencyIds?: string[];
  evaluationId?: string;
  isNewEvaluation?: boolean;
  includeCompanyValues?: boolean;
  surveyNotes?: Record<string, string>;
}

/** Meeting-related navigation state */
export interface MeetingNavState {
  evaluationId?: string;
  employeeId?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function parseOptionalString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function parseOptionalBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

/** Parse assessment state from location.state with full validation */
export function parseAssessmentState(raw: unknown): AssessmentNavState | null {
  if (!isRecord(raw)) return null;
  const surveyNotes = isRecord(raw.surveyNotes)
    ? Object.fromEntries(Object.entries(raw.surveyNotes as Record<string, unknown>).filter(([, v]) => typeof v === "string").map(([k, v]) => [k, v as string]))
    : undefined;
  return {
    employeeId: parseOptionalString(raw.employeeId),
    competencyIds: parseStringArray(raw.competencyIds),
    evaluatedCompetencyIds: parseStringArray(raw.evaluatedCompetencyIds),
    evaluationId: parseOptionalString(raw.evaluationId),
    isNewEvaluation: parseOptionalBoolean(raw.isNewEvaluation),
    includeCompanyValues: parseOptionalBoolean(raw.includeCompanyValues),
    surveyNotes: surveyNotes && Object.keys(surveyNotes).length > 0 ? surveyNotes : undefined,
  };
}

/** Parse meeting state from location.state with validation */
export function parseMeetingState(raw: unknown): MeetingNavState | null {
  if (!isRecord(raw)) return null;
  return {
    evaluationId: parseOptionalString(raw.evaluationId),
    employeeId: parseOptionalString(raw.employeeId),
  };
}

/** Extract a single string field from location.state safely */
export function parseStateField(raw: unknown, field: string): string | undefined {
  if (!isRecord(raw)) return undefined;
  return parseOptionalString(raw[field]);
}
